import React, { useCallback, useEffect, useMemo } from "react";
import { Row, Col } from "react-bootstrap";
import { useFormContext } from "react-hook-form";
import { RootStateOrAny, useDispatch, useSelector } from "react-redux";
import { useParams } from "react-router-dom";

import { Button, HookFormCheckbox, HookFormInputGroup } from "components";
import { CreatedViaTsysApiWarning } from "globalConstants";
import { useTsysStageOnlyVarMutation } from "queries";
import { merchantTerminalActions } from "store";
import {
	minLength4,
	onlyMaxDigits4,
	trimNormalizer,
	required,
} from "validators";

import styles from "../terminalSolution.module.scss";

type TsysStageOnlyVarProps = {
	isLoading: boolean;
	sectionName?: string;
	onSubmitStageOnlyVar?: Function;
	setIsCreationStageOnlyVar?: any;
	refetchTerminalData?: Function | undefined;
	isTidVNumberFromApi: boolean;
};

const TsysStageOnlyVar = ({
	isLoading,
	sectionName,
	onSubmitStageOnlyVar,
	setIsCreationStageOnlyVar,
	refetchTerminalData,
	isTidVNumberFromApi,
}: TsysStageOnlyVarProps) => {
	const params = useParams<any>();
	const dispatch = useDispatch();
	const { register, watch, formState, getValues } = useFormContext();

	const { errors } = formState;

	const values = getValues();

	const isCreateStageOnlyVarInProgress = useSelector(
		(state: RootStateOrAny) =>
			state.merchantTerminal.isCreateStageOnlyVarInProgress,
	);

	const { useCreateTsysStageOnlyVar } = useTsysStageOnlyVarMutation();

	const {
		mutate: createStageOnlyVar,
		isLoading: isCreationStageOnlyVar,
		isSuccess: createStageOnlyVarSuccess,
	} = useCreateTsysStageOnlyVar({
		onLoadingCallback: () => setIsCreationStageOnlyVar?.(true),
		onSuccessCallback: () => refetchTerminalData?.(),
		onSettledCallback: () => setIsCreationStageOnlyVar?.(false),
	});

	const tsysStoreNumber = watch(`${sectionName}.storeNumber`);
	const tidVNumber = watch("tidVNumber");

	const merchantGeneralData = useSelector(
		(state: any) => state.merchantGeneral.generalData,
	);

	useEffect(() => {
		if (isCreateStageOnlyVarInProgress) {
			dispatch(
				merchantTerminalActions.setCreateStageOnlyVarInProgress(false),
			);
			if (params.terminalId) {
				createStageOnlyVar({
					merchantId: merchantGeneralData?.id,
					terminalId: params.terminalId,
				});
			}
		}
	}, [
		createStageOnlyVar,
		dispatch,
		isCreateStageOnlyVarInProgress,
		merchantGeneralData?.id,
		params.terminalId,
	]);

	const isCreateBtnDisabled = useMemo(
		() => tidVNumber || isCreationStageOnlyVar || createStageOnlyVarSuccess,
		[createStageOnlyVarSuccess, tidVNumber, isCreationStageOnlyVar],
	);

	const handleCreateStageOnlyVar = useCallback(async () => {
		if (formState.isDirty || !params.terminalId) {
			await onSubmitStageOnlyVar?.(values);
		}
		dispatch(merchantTerminalActions.setCreateStageOnlyVarInProgress(true));
	}, [
		dispatch,
		formState.isDirty,
		onSubmitStageOnlyVar,
		params.terminalId,
		values,
	]);

	return (
		<div className={styles.section} data-autotest="fileBuildInfoSection">
			<Row className="form-group pb-3">
				<Col md={5}>
					<Button
						type="button"
						variant="outline-primary"
						value="Create Stage only VAR"
						onClick={handleCreateStageOnlyVar}
						disabled={isCreateBtnDisabled}
					/>
				</Col>
				<Col md={{ span: 5, offset: 2 }}>
					<HookFormCheckbox
						register={register}
						id="emailVarSheetToAgent"
						name={`${sectionName}.sendEmail`}
						label="Email VAR sheet to agent"
						disabled={isCreationStageOnlyVar || tsysStoreNumber}
						className="mb-3"
						blurLoading={isLoading}
					/>
				</Col>
			</Row>
			<Row className="form-group">
				<Col md={5}>
					<HookFormInputGroup
						name={`${sectionName}.terminalNumber`}
						register={register}
						label="Terminal #"
						colInput={6}
						colLabel={6}
						blurLoading={isLoading}
						autoComplete="off"
						validator={{
							minLength4,
							required,
						}}
						showRequiredMark
						normalize={onlyMaxDigits4}
						normalizeOnBlur={trimNormalizer}
						readOnly={isCreationStageOnlyVar || isTidVNumberFromApi}
						errors={errors}
						warningText={
							isTidVNumberFromApi ? CreatedViaTsysApiWarning : ""
						}
					/>
				</Col>
				<Col md={{ span: 5, offset: 2 }}>
					<HookFormInputGroup
						name={`${sectionName}.storeNumber`}
						register={register}
						label="Store Number"
						colInput={6}
						colLabel={6}
						blurLoading={isLoading}
						autoComplete="off"
						showRequiredMark
						validator={{ minLength4, required }}
						normalize={onlyMaxDigits4}
						normalizeOnBlur={trimNormalizer}
						readOnly={isCreationStageOnlyVar || isTidVNumberFromApi}
						errors={errors}
						warningText={
							isTidVNumberFromApi ? CreatedViaTsysApiWarning : ""
						}
					/>
				</Col>
			</Row>
		</div>
	);
};

export default TsysStageOnlyVar;
